import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { useStore } from '../../store/store';
import useSubscriptionStatus from '../../hooks/useSubscriptionStatus';
import { formatPeriodDate } from '../../utils/contractPeriods';

// Two visual variants per v3 mockup (Confluence 383516674 Addendum v3,
// docs/prototypes/contact-enrichment.html "Credit balance" scenes):
//   - "row":  label + value pair mirroring SubscriptionDownloadCounter,
//             used inside the HeaderDownloadPopup above the existing
//             "Downloads remaining" row.
//   - "chip": single-line inline pill ("X credits remaining"), used in
//             the Key Contacts action bar between Discover (Stream B)
//             and Reveal selected (TD-2100).
//
// Color states: green (>=20% remaining), yellow/amber (<20%), grey "on hold"
// (limitReached). The hook (useSubscriptionStatus) is the source of truth.
//
// observer required: reads profilerAccessStore.isCognitoContactEnrichment
// (MobX computed).

// Skeleton reserves the counter's footprint so the surrounding layout doesn't
// reflow when the real value resolves.
const renderSkeleton = (variant) =>
  variant === 'row' ? (
    <div className="enrichment-credit-counter enrichment-credit-counter-skeleton">
      <div className="enrichment-credit-counter-label">Contact credits</div>
      <div className="enrichment-credit-counter-value" aria-hidden="true">
        &nbsp;
      </div>
    </div>
  ) : (
    <span className="enrichment-credit-chip enrichment-credit-chip-skeleton" aria-hidden="true">
      &nbsp;
    </span>
  );

// Row variant: "{remaining} / {limit}" stat box mirroring the downloads counter.
const renderRow = (enrichment, insufficient) => {
  const { used, limit, remaining, color, limitReached } = enrichment;
  const display = limitReached ? 0 : remaining;
  // `insufficient` (TD-2271): the selected enrichment would exceed the remaining
  // balance for THIS export. Mirror the limit-reached treatment — grey stat box
  // + alert — so both merged-dialog boxes grey together.
  const isAlert = limitReached || insufficient;
  const boxColor = insufficient ? 'grey' : color;
  return (
    <div
      className={`enrichment-credit-counter enrichment-credit-counter-${boxColor}`}
      role={isAlert ? 'alert' : 'status'}
      aria-live={isAlert ? 'assertive' : 'polite'}
    >
      <div className="enrichment-credit-counter-label">Contact credits</div>
      <div
        className="enrichment-credit-counter-value"
        title={`${used.toLocaleString()} of ${limit.toLocaleString()} credits used`}
      >
        {display.toLocaleString()} / {limit.toLocaleString()}
      </div>
      {enrichment.resetsOn && (
        <div className="enrichment-credit-reset">
          Resets on {formatPeriodDate(enrichment.resetsOn)}
        </div>
      )}
    </div>
  );
};

// Chip variant: inline "X credits remaining" pill (grey "on hold" at the limit).
const renderChip = (enrichment) => {
  const { remaining, color, limitReached } = enrichment;
  const text = limitReached
    ? '0 credits - on hold'
    : `${remaining.toLocaleString()} credits remaining`;
  return (
    <span
      className={`enrichment-credit-chip enrichment-credit-chip-${color}`}
      aria-label={text}
      role={limitReached ? 'alert' : 'status'}
    >
      {text}
    </span>
  );
};

const EnrichmentCreditCounter = observer((props) => {
  // Controlled mode (TD-2351): when the parent passes `enrichment`, the counter
  // renders from it and suppresses its own fetch — so a parent owning a single
  // useSubscriptionStatus instance can refresh the counter (e.g. after a bulk
  // reveal) without a page reload. Omitted → self-fetches as before
  // (HeaderDownloadPopup, EnrichmentDownloadDialog, SearchResults,
  // ProfilerProjectView).
  const {
    variant,
    insufficient = false,
    enrichment: enrichmentProp,
    isLoading: isLoadingProp
  } = props;
  const { profilerAccessStore } = useStore();
  const enabled = profilerAccessStore.isCognitoContactEnrichment;
  // `enrichment === undefined` (not `null`) is the controlled switch: a parent
  // with access but still loading passes `null`, which must stay controlled so
  // the internal fetch remains suppressed.
  const isControlled = enrichmentProp !== undefined;
  const hook = useSubscriptionStatus({ enabled: enabled && !isControlled });
  const enrichment = isControlled ? enrichmentProp : hook.enrichment;
  const isLoading = isControlled ? !!isLoadingProp : hook.isLoading;

  if (!enabled) return null;
  if (isLoading && !enrichment) return renderSkeleton(variant);
  if (!enrichment?.limit) return null;
  return variant === 'row' ? renderRow(enrichment, insufficient) : renderChip(enrichment);
});

EnrichmentCreditCounter.propTypes = {
  variant: PropTypes.oneOf(['row', 'chip']).isRequired,
  // Row variant only (TD-2271): force the grey "on hold" treatment when the
  // pending export would exceed the remaining balance.
  insufficient: PropTypes.bool,
  // Controlled mode (TD-2351): when provided, the counter renders from this and
  // does not fetch. Shape mirrors useSubscriptionStatus().enrichment.
  enrichment: PropTypes.shape({
    used: PropTypes.number,
    limit: PropTypes.number,
    remaining: PropTypes.number,
    color: PropTypes.string,
    limitReached: PropTypes.bool,
    periodEnd: PropTypes.string,
    resetsOn: PropTypes.string
  }),
  // Controlled mode (TD-2351): loading flag paired with `enrichment`.
  isLoading: PropTypes.bool
};

export default EnrichmentCreditCounter;
